import { ExtractComponentProp } from '@approvalmax/types';
import { BodyCellProps, Select, Table, Text } from '@approvalmax/ui/src/components';
import { useSetAtom } from 'jotai';
import { selectors } from 'modules/common';
import { useSelector } from 'modules/react-redux';
import { memo, useCallback } from 'react';
import { useToggle } from 'react-use';
import { noValue } from 'shared/constants';
import { useTimeZoneSelect } from 'shared/hooks/useTimeZoneSelect';

import { userListDataState } from '../../UserList.states';
import { UserRow } from '../../UserList.types';
import { messages } from './DelegateTimeZoneCell.messages';

export const DelegateTimeZoneCell = memo<BodyCellProps<UserRow>>((props) => {
    const { hovered, item, value } = props;

    const [open, toggleOpen] = useToggle(false);
    const setUserListData = useSetAtom(userListDataState);

    const company = useSelector(selectors.navigation.getActiveCompany);

    const isExpired = company.flags.isExpired && !company.flags.isGraceSubscription;
    const disabled = company.flags.isRetired || company.isReadonly || isExpired;

    const timeZoneSelectProps = useTimeZoneSelect(company.id);

    const handleChange = useCallback<ExtractComponentProp<typeof Select, 'onChange'>>(
        (newValue) => {
            setUserListData((prevData) =>
                prevData.map((row) => {
                    if (row.id === item.id) {
                        return { ...row, delegateTimeZone: newValue as string };
                    }

                    return row;
                })
            );
        },
        [item.id, setUserListData]
    );

    const handleOpen = useCallback(() => {
        toggleOpen();
    }, [toggleOpen]);

    return (
        <Table.Cell {...props} title={messages.tooltip}>
            {item.delegateUserId ? (
                <Select
                    size='small'
                    dropdownWidth='max-content'
                    clearable={false}
                    hideEndIcon={!(open || hovered)}
                    bordered={open || hovered}
                    disabled={disabled}
                    value={value}
                    placeholder={messages.placeholder}
                    onOpen={handleOpen}
                    onChange={handleChange}
                    {...timeZoneSelectProps}
                />
            ) : (
                <Text fontWeight='regular' font='body' fontSize='small'>
                    {noValue}
                </Text>
            )}
        </Table.Cell>
    );
});

DelegateTimeZoneCell.displayName = 'DelegateTimeZoneCell';
